/**
 * Boot-time guest nudge.
 *
 * When the app launches with an unregistered (guest) Jest player, present
 * Jest's hosted login/registration overlay to nudge them toward an account.
 * This is a soft prompt, not a gate: guests keep full app access (Jest
 * compliance), so a dismissed overlay simply leaves them a guest — boot never
 * blocks or dead-ends on the outcome.
 *
 * On a successful login we re-derive the FantasyPRIME session from the
 * now-registered Jest player so the running app reflects jestGuest=false
 * without a reload, mirroring the manual Register flow in pages/login.
 */
import { useAuthStore } from "@/stores/auth.store";
import { authenticateWithJest } from "./jest-auth";
import { awaitRegisteredSignedPlayer } from "./jest-registration";
import { isRegisteredUser } from "./jest-guest";
import { scheduleAfterRegistration } from "./notifications";
import { identifyUser } from "./analytics";

export async function promptGuestLogin(sdk: JestSDKApi): Promise<void> {
  let player: JestSDKPlayer;
  try {
    // getPlayer() reads Jest's local cache (synchronous on the live SDK);
    // `await` tolerates both the sync and promise shapes.
    player = await sdk.getPlayer();
  } catch {
    // Can't determine status — skip the nudge rather than risk a bad prompt.
    return;
  }

  // Already registered: nothing to nudge.
  if (player.registered) return;

  try {
    // Jest's hosted login overlay. Rejects if the player dismisses it.
    await sdk.login();
  } catch {
    // Dismissed — not an error. Stay a full-access guest.
    return;
  }

  // login() can resolve before getPlayerSigned() reflects registered=true, so
  // poll briefly for a registered token (the same lag the manual Register flow
  // guards against), then re-auth regardless of the outcome.
  const signed = await awaitRegisteredSignedPlayer(sdk, {
    attempts: 4,
    delayMs: 400,
  });

  try {
    const auth = await authenticateWithJest(sdk, {
      playerSigned: signed.playerSigned,
    });
    const store = useAuthStore.getState();
    store.setAuth(auth.accessToken, auth.sessionId);
    if (auth.user) {
      store.setUser(auth.user);
      // re-identify so PostHog's jest_guest flips to false on registration
      // (same distinct_id, refreshed person properties).
      identifyUser(auth.user);
    }

    // Only schedule the post-registration notifications once the backend has
    // actually cleared jestGuest (registration may not take in mocked hosts).
    if (isRegisteredUser(auth.user)) {
      void scheduleAfterRegistration(sdk);
    }
  } catch (err) {
    console.error("[jest-client] Guest login re-auth failed:", err);
  }
}
